import {
  kustomerOrgNameSchema,
  type KustomerIntegrationRequest,
} from "@repo/app-types/entities/integration";
import {
  DEFAULT_KNOWLEDGE_BASE_SYNC_SCHEDULE,
  INITIAL_KNOWLEDGE_BASE_SYNC_AUTOSYNC_ENABLED,
  type KnowledgeBaseSyncActiveRun,
  type KustomerKnowledgeBaseSyncIntegration,
} from "@repo/app-types/entities/knowledgeBaseSync";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import type { RecurringSchedule } from "@repo/app-types/scheduling/recurringSchedule";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Link } from "@repo/ui/Link/routerLink";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import isEqual from "lodash-es/isEqual.js";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { useAllKnowledgeBasesQuery } from "../../../api/entities/knowledgeBase.js";
import {
  getKnowledgeBaseSyncIntegrationErrorMessage,
  useCreateKnowledgeBaseSyncIntegrationMutation,
  useTriggerKnowledgeBaseSyncMutation,
  useUpdateKnowledgeBaseSyncIntegrationMutation,
} from "../../../api/entities/knowledgeBaseSyncIntegration.js";
import { appPaths } from "../../../app/appPaths.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { KnowledgeBaseSyncIntegrationScheduleFields } from "./knowledgeBaseSyncIntegrationScheduleFields.js";

interface KustomerFormState {
  orgName: string;
  apiKey: string;
  syncSchedule: RecurringSchedule;
  autosyncEnabled: boolean;
}

export interface KustomerIntegrationSettingsProps {
  integration: KustomerKnowledgeBaseSyncIntegration | null;
  activeSyncRun: KnowledgeBaseSyncActiveRun | null;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
}

export const KustomerIntegrationSettings: FC<
  KustomerIntegrationSettingsProps
> = (props) => {
  const { integration, activeSyncRun, isLoading, isError, onReload } = props;
  const [createIntegration, createState] =
    useCreateKnowledgeBaseSyncIntegrationMutation();
  const [updateIntegration, updateState] =
    useUpdateKnowledgeBaseSyncIntegrationMutation();
  const [triggerSync, triggerState] = useTriggerKnowledgeBaseSyncMutation();
  const isSaving = createState.isLoading || updateState.isLoading;
  const { data: knowledgeBases, refetch: refetchKnowledgeBases } =
    useAllKnowledgeBasesQuery();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const hadActiveSyncRunRef = useRef(false);
  const initialState = useMemo<KustomerFormState>(
    () => ({
      orgName: integration?.org_name ?? "",
      apiKey: "",
      syncSchedule:
        integration?.sync_schedule ?? DEFAULT_KNOWLEDGE_BASE_SYNC_SCHEDULE,
      autosyncEnabled:
        integration?.autosync_enabled ??
        INITIAL_KNOWLEDGE_BASE_SYNC_AUTOSYNC_ENABLED,
    }),
    [integration],
  );
  const [state, setState] = useState(initialState);
  const syncedKnowledgeBase = useMemo(
    () =>
      knowledgeBases?.find(
        (knowledgeBase) =>
          integration?.knowledge_base_ids.includes(
            knowledgeBase.metadata?.external_provider?.external_id ?? "",
          ) &&
          knowledgeBase.metadata?.external_provider?.provider === "kustomer",
      ) ?? null,
    [integration?.knowledge_base_ids, knowledgeBases],
  );

  useEffect(() => setState(initialState), [initialState]);
  useEffect(() => {
    if (activeSyncRun) {
      hadActiveSyncRunRef.current = true;
      return;
    }
    if (hadActiveSyncRunRef.current) {
      hadActiveSyncRunRef.current = false;
      void refetchKnowledgeBases();
    }
  }, [activeSyncRun, refetchKnowledgeBases]);

  if (isLoading) {
    return <Loader style={LoaderStyle.SPINNER} />;
  }
  if (isError) {
    return (
      <div className="flex max-w-3xl flex-col items-start gap-3 rounded-lg border border-border-light bg-card p-4">
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load your Kustomer configuration.
        </p>
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.SMALL}
          onClick={onReload}
        >
          Try again
        </Button>
      </div>
    );
  }

  const orgNameResult = kustomerOrgNameSchema.safeParse(state.orgName);
  const hasChanges =
    state.orgName.trim() !== initialState.orgName ||
    state.apiKey.length > 0 ||
    !isEqual(state.syncSchedule, initialState.syncSchedule) ||
    state.autosyncEnabled !== initialState.autosyncEnabled;
  const canSave =
    orgNameResult.success &&
    (Boolean(integration) || state.apiKey.trim().length > 0);
  const requiredAction = integration
    ? PermissionActions.UPDATE
    : PermissionActions.CREATE;

  const handleSave = async () => {
    setSaveError(null);
    try {
      const request: KustomerIntegrationRequest = {
        org_name: state.orgName.trim(),
        ...(state.apiKey.trim() ? { api_key: state.apiKey.trim() } : {}),
        knowledge_base_ids: [],
        sync_schedule: state.syncSchedule,
        autosync_enabled: state.autosyncEnabled,
      };
      if (integration) {
        await updateIntegration({
          provider: "kustomer",
          integrationId: integration.integration_id,
          configuration: request,
        }).unwrap();
      } else {
        await createIntegration({
          provider: "kustomer",
          configuration: request,
        }).unwrap();
      }
      setState((current) => ({ ...current, apiKey: "" }));
    } catch (error) {
      setSaveError(
        getKnowledgeBaseSyncIntegrationErrorMessage(
          error,
          "Could not connect to Kustomer — check the credentials and try again.",
        ),
      );
    }
  };

  const handleSyncNow = async () => {
    if (!integration) return;
    setSyncError(null);
    try {
      await triggerSync({
        provider: "kustomer",
        integrationId: integration.integration_id,
      }).unwrap();
    } catch (error) {
      setSyncError(
        getKnowledgeBaseSyncIntegrationErrorMessage(
          error,
          "Could not start the Kustomer sync — please try again.",
        ),
      );
    }
  };

  return (
    <Can
      I={requiredAction}
      a={PermissionSubjects.INTEGRATIONS}
      passThrough={true}
    >
      {(allowed: boolean) => {
        const isDisabled = !allowed || isSaving;
        return (
          <div className="max-w-3xl space-y-5">
            <Input
              label="API organization subdomain"
              accessibleLabel="API organization subdomain"
              value={state.orgName}
              placeholder="acme"
              description="The {orgName} in {orgName}.api.kustomerapp.com."
              errorDescription={
                state.orgName.length > 0 && !orgNameResult.success
                  ? orgNameResult.error.issues[0]?.message
                  : undefined
              }
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  orgName: event.target.value,
                }))
              }
              isDisabled={isDisabled}
            />

            <Input
              label="API key"
              accessibleLabel="API key"
              type="password"
              autoComplete="off"
              value={state.apiKey}
              placeholder={integration ? "••••••" : ""}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  apiKey: event.target.value,
                }))
              }
              isDisabled={isDisabled}
            />

            {integration ? (
              <>
                {syncedKnowledgeBase ? (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Knowledge base</p>
                    <Link
                      href={appPaths.settingsKnowledgeBase.build({
                        knowledgeBaseId: syncedKnowledgeBase.id,
                      })}
                      className="text-sm font-medium text-indigo-600 hover:underline"
                    >
                      {syncedKnowledgeBase.name}
                    </Link>
                  </div>
                ) : null}

                <KnowledgeBaseSyncIntegrationScheduleFields
                  syncSchedule={state.syncSchedule}
                  autosyncEnabled={state.autosyncEnabled}
                  onScheduleChange={(syncSchedule) =>
                    setState((current) => ({ ...current, syncSchedule }))
                  }
                  onAutosyncEnabledChange={(autosyncEnabled) =>
                    setState((current) => ({ ...current, autosyncEnabled }))
                  }
                  isDisabled={isDisabled}
                />
                <Can
                  I={PermissionActions.CREATE}
                  a={PermissionSubjects.KNOWLEDGE_BASES}
                  passThrough={true}
                >
                  {(canSync) => (
                    <Button
                      variant={ButtonVariant.Secondary}
                      onClick={() => void handleSyncNow()}
                      isLoading={triggerState.isLoading}
                      isDisabled={
                        !canSync ||
                        triggerState.isLoading ||
                        activeSyncRun !== null ||
                        hasChanges
                      }
                    >
                      Sync now
                    </Button>
                  )}
                </Can>
                {activeSyncRun ? (
                  <p className="text-sm text-muted-foreground" role="status">
                    {activeSyncRun.status === "running"
                      ? "Sync in progress."
                      : "Sync scheduled."}
                  </p>
                ) : null}
                {hasChanges ? (
                  <p className="text-sm text-muted-foreground">
                    Save changes before syncing.
                  </p>
                ) : null}
              </>
            ) : null}

            {saveError ? (
              <p className="text-sm text-red-600">{saveError}</p>
            ) : null}
            {syncError ? (
              <p className="text-sm text-red-600">{syncError}</p>
            ) : null}

            {allowed && hasChanges ? (
              <div className="flex items-center gap-2">
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={() => void handleSave()}
                  isDisabled={!canSave || isSaving}
                >
                  {integration ? "Save" : "Connect Kustomer"}
                </Button>
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => {
                    setState(initialState);
                    setSaveError(null);
                  }}
                  isDisabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            ) : null}
          </div>
        );
      }}
    </Can>
  );
};
